import { type Treaty, treaty } from '@elysiajs/eden';
import type { App } from '@needful-things-mvp/api';
import { DOMAINS, type Environment } from '@needful-things-mvp/constants';

/** Thrown when the browser blocks the OAuth popup. */
export class PopupBlockedError extends Error {
  readonly name = 'PopupBlockedError';
  constructor() {
    super('The authentication popup was blocked by the browser.');
  }
}

/** Thrown when the user closes the popup before finishing. */
export class OAuthCancelledError extends Error {
  readonly name = 'OAuthCancelledError';
  constructor() {
    super('The authentication popup was closed before completing.');
  }
}

/** Thrown when the flow doesn't complete within the timeout. */
export class OAuthTimeoutError extends Error {
  readonly name = 'OAuthTimeoutError';
  constructor() {
    super('The authentication flow timed out.');
  }
}

/** Thrown when the callback payload is missing required fields or fails verification. */
export class OAuthInvalidResponseError extends Error {
  readonly name = 'OAuthInvalidResponseError';
  constructor() {
    super('The OAuth callback returned a malformed payload.');
  }
}

/** Thrown when the server rejects the initiation request. */
export class OAuthInitFailedError extends Error {
  readonly name = 'OAuthInitFailedError';
  constructor(message = 'OAuth initiation failed') {
    super(message);
  }
}

/** Persists the session id between page loads. */
export interface SessionStore {
  get(): string | null;
  set(sessionId: string): void | Promise<void>;
  clear(): void | Promise<void>;
}

class BrowserSessionStore implements SessionStore {
  constructor(private readonly key: string) {}

  get(): string | null {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(this.key);
  }

  set(sessionId: string): void {
    if (typeof localStorage !== 'undefined') localStorage.setItem(this.key, sessionId);
  }

  clear(): void {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(this.key);
  }
}

/** The user returned by the OAuth callback. */
export interface OAuthUser {
  id: string;
  name: string | null;
  email: string;
  role: string;
  status: string;
  picture?: string | null;
}

/** The provider profile returned by the OAuth callback. */
export interface OAuthProfile {
  id: string;
  name: string;
  email: string;
  picture?: string;
  provider: string;
}

/** Resolved result of a successful OAuth flow. */
export interface OAuthResult {
  user: OAuthUser;
  /** Absent when the server issues an httpOnly session cookie instead. */
  sessionId?: string;
  profile: OAuthProfile;
  fingerprint?: string;
}

export interface SDKOptions {
  /** Custom persistence for the session id (defaults to localStorage). */
  sessionStore?: SessionStore;
  /** Explicit API base URL; overrides the environment mapping. */
  baseUrl?: string;
}

/**
 * Supported environment types for the SDK.
 * - `'local'`: Local development server (http://localhost:3333)
 * - `Environment`: Production or other configured environments
 */
export type SDKEnvironment = 'local' | Environment;

type AppTreaty = Treaty.Create<App>;

/**
 * Type-safe SDK client for the needful-things-mvp API.
 *
 * Provides a Treaty-based HTTP client with automatic environment-based URL resolution
 * and full type safety for all API endpoints.
 *
 * @example
 * ```typescript
 * // Use the default instance
 * import { sdk } from '@needful-things-mvp/sdk';
 *
 * const { data } = await sdk.api.profile.patch({ age: 21 });
 *
 * // Create a custom instance
 * const prodClient = new SDK('production');
 * await prodClient.api.users.index.get();
 *
 * // Switch environments dynamically
 * sdk.setEnvironment('production');
 * await sdk.api.companies.index.get();
 * ```
 */
export class SDK {
  private client: AppTreaty;
  sessionId: string | null = null;
  private readonly SESSION_KEY = 'needful-things-mvp_session_id';
  private readonly FINGERPRINT_KEY = 'needful-things-mvp_oauth_fp';
  private readonly sessionStore: SessionStore;
  private baseUrlOverride: string | null;
  private onSessionExpired: ((message?: string) => void) | null = null;

  /**
   * Creates a new SDK instance.
   *
   * @param environment - The environment to connect to. Defaults to 'local'.
   * @param options - Optional configuration (e.g. a custom session store).
   */
  constructor(
    public environment: SDKEnvironment = 'local',
    options: SDKOptions = {},
  ) {
    this.sessionStore = options.sessionStore ?? new BrowserSessionStore(this.SESSION_KEY);
    this.baseUrlOverride = options.baseUrl ? options.baseUrl.replace(/\/+$/, '') : null;
    this.client = this.createClient();
  }

  /**
   * Creates a Treaty client with proper authentication headers.
   *
   * @returns The Treaty client configured for the current environment.
   */
  private createClient(): AppTreaty {
    const client = treaty<App>(this.baseUrl, {
      // Send/receive cookies so the OAuth state + PKCE verifier set by the
      // initiation endpoint are stored on the API origin.
      fetch: { credentials: 'include' },
      headers: () => {
        if (this.sessionId) {
          return {
            authorization: `Bearer ${this.sessionId}`,
          };
        }
        return undefined;
      },
      onResponse: ({ status }) => {
        if (status === 401 || status === 403) {
          this.handleSessionExpired();
        }
      },
    });

    return client;
  }

  /**
   * Handles session expiration by clearing local state and calling the callback.
   *
   * @param message - Optional message to pass to the callback
   */
  private handleSessionExpired(message?: string): void {
    this.clearSession();
    if (this.onSessionExpired) {
      this.onSessionExpired(message);
    }
  }

  /**
   * Sets a callback to be called when the session expires.
   *
   * @param callback - The function to call when session expiration is detected. Receives an optional message parameter.
   *
   * @example
   * ```typescript
   * sdk.setOnSessionExpired((message) => {
   *   // Show notification
   *   showToast(message || 'Session expired');
   *   // Redirect to login page
   *   router.path = '/login';
   * });
   * ```
   */
  setOnSessionExpired(callback: (message?: string) => void): void {
    this.onSessionExpired = callback;
  }

  /**
   * Checks if an error indicates session expiration and triggers the callback if so.
   *
   * @param error - The error object from an API call
   * @returns true if the error was a session error, false otherwise
   *
   * @example
   * ```typescript
   * const { data, error } = await sdk.api.profile.get();
   * if (sdk.checkSessionError(error)) {
   *   return; // User will be logged out automatically
   * }
   * ```
   */
  checkSessionError(error: any): boolean {
    if (!error) return false;

    // Check error status
    if (error.status === 401 || error.status === 403) {
      const errorMessage =
        typeof error.value === 'string' ? error.value : JSON.stringify(error.value || '');

      const isSessionError =
        errorMessage.includes('session') ||
        errorMessage.includes('banned') ||
        errorMessage.includes('Authentication required');

      if (isSessionError) {
        // Extract user-friendly message
        let message = 'Your session has expired. Please log in again.';
        if (errorMessage.includes('banned')) {
          message = 'Your account has been banned. Please contact support.';
        } else if (errorMessage.includes('No session token provided')) {
          message = 'Please log in to continue.';
        }

        this.handleSessionExpired(message);
        return true;
      }
    }

    return false;
  }

  /**
   * Gets the base URL for the current environment.
   *
   * @returns The full API base URL (including protocol and domain).
   */
  get baseUrl() {
    if (this.baseUrlOverride) return this.baseUrlOverride;
    if (this.environment === 'local') {
      return 'http://localhost:3333';
    }

    return `https://${DOMAINS.api[this.environment]}`;
  }

  /**
   * Points the client at an explicit API base URL, overriding the environment
   * mapping. Use when the URL is provided at build time (e.g. `VITE_API_URL`).
   * Passing `undefined` restores the environment-based URL.
   */
  setBaseUrl(baseUrl?: string): void {
    this.baseUrlOverride = baseUrl ? baseUrl.replace(/\/+$/, '') : null;
    this.client = this.createClient();
  }

  /**
   * Changes the environment and reinitializes the client.
   *
   * This will update the base URL and create a new Treaty client instance.
   *
   * @param environment - The new environment to switch to.
   *
   * @example
   * ```typescript
   * const client = new SDK('local');
   * client.setEnvironment('production'); // Now points to production API
   * ```
   */
  setEnvironment(environment: SDKEnvironment): void {
    this.environment = environment;
    this.baseUrlOverride = null;
    this.client = this.createClient();
  }

  /**
   * Restores the session ID from localStorage.
   *
   * Call this method during app initialization (e.g., in hydrate) to restore
   * a persisted session from a previous visit.
   *
   * @returns The restored session ID, or null if no session was found.
   *
   * @example
   * ```typescript
   * async hydrate() {
   *   sdk.restoreSession();
   *   await this.checkAuth();
   * }
   * ```
   */
  restoreSession(): string | null {
    this.sessionId = this.sessionStore.get();
    return this.sessionId;
  }

  /**
   * Sets the session ID in memory only (not persisted to localStorage).
   *
   * Use this for temporary sessions during onboarding flow that shouldn't
   * survive a page refresh until the user completes onboarding.
   *
   * @param sessionId - The session ID to set in memory.
   *
   * @example
   * ```typescript
   * sdk.setSessionInMemory('abc123xyz');
   * ```
   */
  setSessionInMemory(sessionId: string): void {
    this.sessionId = sessionId;
  }

  /**
   * Sets the session ID and persists it to localStorage.
   *
   * This will automatically add the Authorization header to all subsequent API requests.
   *
   * @param sessionId - The session ID to set and persist.
   *
   * @example
   * ```typescript
   * sdk.setSession('abc123xyz');
   * ```
   */
  setSession(sessionId: string): void {
    this.sessionId = sessionId;
    void this.sessionStore.set(sessionId);
  }

  /**
   * Persists the current in-memory session to localStorage.
   *
   * Call this after the user completes onboarding to make the session
   * survive page refreshes.
   *
   * @example
   * ```typescript
   * sdk.persistSession();
   * ```
   */
  persistSession(): void {
    if (this.sessionId) {
      void this.sessionStore.set(this.sessionId);
    }
  }

  /**
   * Clears the session ID from memory and localStorage.
   *
   * This will remove the Authorization header from subsequent API requests.
   *
   * @example
   * ```typescript
   * sdk.clearSession();
   * ```
   */
  clearSession(): void {
    this.sessionId = null;
    void this.sessionStore.clear();
  }

  /**
   * OAuth authentication flows.
   *
   * @example
   * ```ts
   * const { user, sessionId, profile } = await sdk.auth.oauth2.initiate('google');
   * ```
   */
  get auth() {
    return {
      oauth2: {
        /**
         * Opens an OAuth popup and resolves with `{ user, sessionId, profile }`.
         *
         * The popup is opened synchronously before any network call so the browser
         * counts it as a user gesture (required by iOS Safari and strict popup
         * blockers). Call `initiate()` synchronously from the click/tap handler —
         * do not `await` anything before calling it. On success the `sessionId` is
         * persisted to the configured {@link SessionStore} and set on the client.
         *
         * @throws {PopupBlockedError} If the popup couldn't be opened.
         * @throws {OAuthCancelledError} If the user closed the popup early.
         * @throws {OAuthTimeoutError} If the flow didn't complete in time.
         * @throws {OAuthInvalidResponseError} If the callback payload was malformed.
         * @throws {OAuthInitFailedError} If the server rejected the initiation.
         */
        initiate: (provider: string, opts?: { window?: Window }): Promise<OAuthResult> => {
          const popup =
            opts?.window ??
            (typeof window !== 'undefined'
              ? window.open('about:blank', '_blank', 'width=500,height=600')
              : null);

          if (!popup) return Promise.reject(new PopupBlockedError());

          return this.completeOAuth(provider, popup);
        },
      },
    };
  }

  private async completeOAuth(provider: string, popup: Window): Promise<OAuthResult> {
    const storedFingerprint =
      typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(this.FINGERPRINT_KEY) : null;
    const origin = typeof window !== 'undefined' ? window.location.origin : this.baseUrl;

    const { data, error } = await this.client.auth.oauth2({ provider }).get({
      query: {
        origin,
        ...(storedFingerprint ? { fingerprint: storedFingerprint } : {}),
      },
    });

    if (error) {
      if (!popup.closed) popup.close();
      const value = error.value as { message?: string } | undefined;
      throw new OAuthInitFailedError(value?.message ?? 'OAuth initiation failed');
    }

    const url = data?.url;
    if (!url) {
      if (!popup.closed) popup.close();
      throw new OAuthInitFailedError('OAuth initiation did not return a URL');
    }

    // The user may have closed the popup while we were fetching the URL.
    if (popup.closed) throw new OAuthCancelledError();

    if (data?.fingerprint && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(this.FINGERPRINT_KEY, data.fingerprint);
    }

    popup.location.href = url;

    const payload = await new Promise<OAuthResult>((resolve, reject) => {
      const messageOrigin = new URL(this.baseUrl).origin;
      let settled = false;

      const cleanup = () => {
        clearInterval(interval);
        clearTimeout(timeout);
        window.removeEventListener('message', handleMessage);
      };

      const closePopup = () => {
        // The success page listens for a 'close' message and self-closes too.
        try {
          popup.postMessage('close', messageOrigin);
        } catch {
          // popup may already be gone
        }
        if (!popup.closed) popup.close();
      };

      const handleMessage = (event: MessageEvent) => {
        if (settled) return;
        if (
          event.origin !== messageOrigin ||
          event.data?.type !== 'oauth-callback' ||
          !event.data?.data
        ) {
          return;
        }

        const result = event.data.data as OAuthResult;
        settled = true;
        cleanup();
        closePopup();

        if (!result?.user || !result?.profile) {
          reject(new OAuthInvalidResponseError());
          return;
        }
        if (storedFingerprint && result.fingerprint && result.fingerprint !== storedFingerprint) {
          reject(new OAuthInvalidResponseError());
          return;
        }

        resolve(result);
      };

      window.addEventListener('message', handleMessage);

      const interval = setInterval(() => {
        if (popup.closed && !settled) {
          settled = true;
          cleanup();
          reject(new OAuthCancelledError());
        }
      }, 500);

      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          cleanup();
          closePopup();
          reject(new OAuthTimeoutError());
        }
      }, 180_000);
    });

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(this.FINGERPRINT_KEY);
    }

    // Cookie-based sessions aren't exposed to JS; only persist a token if one
    // was actually returned (e.g. non-browser clients).
    if (payload.sessionId) {
      await this.sessionStore.set(payload.sessionId);
      this.sessionId = payload.sessionId;
    }
    return payload;
  }

  /**
   * Gets the Treaty client instance with full type safety.
   *
   * Access all API endpoints through this property with autocomplete and type checking.
   *
   * @returns The Treaty client configured for the current environment.
   *
   * @example
   * ```typescript
   * const { data, error } = await sdk.api.auth.login.post({
   *   email: 'user@example.com',
   *   password: 'secret123'
   * });
   * ```
   */
  get api(): AppTreaty {
    return this.client;
  }
}

/**
 * Default SDK instance configured for local development.
 *
 * Use this for quick access without creating a new SDK instance.
 *
 * @example
 * ```typescript
 * import { sdk } from '@needful-things-mvp/sdk';
 *
 * // Make API calls
 * const { data } = await sdk.api.profile.get();
 *
 * // Switch to production
 * sdk.setEnvironment('production');
 * ```
 */
export const sdk = new SDK();
